import type { User } from 'firebase/auth';
import { collection, doc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { ensureStripeCustomerForOrg } from '@/lib/ensureStripeCustomer';
import type { UserRole } from '@/types/team';
import { logger } from '@/lib/logger';

/** Creates `users/{uid}` (OWNER) and a new `organizations/{orgId}` doc. */
export async function provisionNewOwnerUser(user: User, displayName: string): Promise<void> {
    const orgId = crypto.randomUUID();

    await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        displayName,
        role: 'OWNER' as UserRole,
        orgId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });

    await setDoc(doc(db, 'organizations', orgId), {
        id: orgId,
        name: `${displayName}'s Restoration`,
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        settings: { theme: 'dark' },
    });

    try {
        await ensureStripeCustomerForOrg(orgId);
    } catch (err) {
        logger.error('[provisionNewOwnerUser] ensureStripeCustomerForOrg failed:', err);
    }
}

/**
 * The token of a pending invite addressed to this email, or null (WEB-67).
 * The rules let a signed-in user list invites carrying their own token email.
 * Throws when the query fails: the caller must not create an OWNER org for
 * someone it could not check.
 */
export async function findPendingInviteToken(email: string | null | undefined): Promise<string | null> {
    const normalized = (email ?? '').toLowerCase().trim();
    if (!normalized) return null;
    const snap = await getDocs(
        query(collection(db, 'invites'), where('email', '==', normalized), where('status', '==', 'pending')),
    );
    return snap.empty ? null : snap.docs[0].id;
}

/**
 * Self-registration on /login (WEB-67): an invitee who signs up there instead
 * of following their invite used to become OWNER of a stray new org, which
 * also skipped the onboarding wizard's invite redirect (it only runs without
 * an orgId). Returns the invite token to route into instead, or null after
 * provisioning a new owner as before.
 */
export async function provisionOwnerUnlessInvited(user: User, displayName: string): Promise<string | null> {
    const token = await findPendingInviteToken(user.email);
    if (token) return token;
    await provisionNewOwnerUser(user, displayName);
    return null;
}
